import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Link
} from '@mui/material';
import { Pagination } from '@mui/material';
import {
  getUserHistoryAsync,
  clearUserHistory,
  selectBalance
} from '../../redux/slices/balanceSlice';
import { formatDate } from '../../resources/functions';
import TableCell from '../../components/TableCell';
import PaymentDetailsModal from '../../modals/PaymentDetailsModal/PaymentDetailsModal';
import { History } from '../../resources/types/balanceTypes';

const limit = 10;

interface Props {
  userId: string | null;
}

const getTransactionTypeLabel = (
  t: TFunction,
  type: string,
  paymentId?: string
): string => {
  const typeMap: Record<string, string> = {
    stopInZone: t('userModal.txCashback'),
    contract: t('userModal.txRide'),
    pay: paymentId
      ? t('userModal.txTopUpWithId', { id: paymentId })
      : t('userModal.txTopUp'),
    admin: t('userModal.txTopUpByAdmin'),
    game: t('userModal.txGamePrize'),
    fortuneWheel: t('userModal.txFortuneWheel'),
    subscription: t('userModal.txSubscription')
  };
  
  return typeMap[type] || type;
};

const UserHistoryContent = ({ userId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { history } = useSelector(selectBalance);

  const [page, setPage] = useState(1);
  const [pending, setPending] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    if (userId) {
      (async () => {
        setPending(true);
        await dispatch(
          getUserHistoryAsync({ user_id: userId, limit, page, active: false })
        );
        setPending(false);
      })();
    }

    return () => {
      dispatch(clearUserHistory());
    };
  }, [dispatch, userId, page]);

  const paginationCount = useMemo(
    () => Math.ceil(history.count / limit),
    [history.count]
  );

  const changePage = useCallback(
    (event: any, nextPage: number) => setPage(nextPage),
    []
  );

  const handlePaymentClick = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedPaymentId(null);
  };

  const renderTransactionType = (item: History) => {
    const paymentId = item.liq_pay_order?.payment_id;
    const label = getTransactionTypeLabel(t, item.type, paymentId);

    if (item.type === 'pay' && paymentId) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={() => handlePaymentClick(paymentId)}
          sx={{
            textDecoration: 'none',
            cursor: 'pointer',
            color: '#5073F8',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {label}
        </Link>
      );
    }

    return label;
  };

  return (
    <Box sx={{ p: 3 }}>
      {history.items.length ? (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('userModal.colType')}</TableCell>
                  <TableCell>{t('userModal.colAmount')}</TableCell>
                  <TableCell>{t('userModal.colDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.items.map(item => (
                  <TableRow key={item.createdAt}>
                    <TableCell>{renderTransactionType(item)}</TableCell>
                    <TableCell>{item.amount}</TableCell>
                    <TableCell>
                      {formatDate(item.createdAt, { time: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={paginationCount}
            style={{ marginTop: 10 }}
            onChange={changePage}
          />
        </>
      ) : (
        !pending && (
          <Typography color='textSecondary' variant='h5'>
            {t('userModal.emptyTransactions')}
          </Typography>
        )
      )}

      <PaymentDetailsModal
        open={isPaymentModalOpen}
        onClose={handleClosePaymentModal}
        paymentId={selectedPaymentId}
      />
    </Box>
  );
};

export default UserHistoryContent;
